<template>
  <component
    :is="bannerComponent"
    v-bind="bannerProps"
    @close="close"
    @dismiss-promotion="dismissPromotionBanner"
    @cta-clicked="trackCtaClicked"
  >
    <template #primaryCta>
      <slot name="primaryCta"></slot>
    </template>
  </component>
</template>

<script>
import { UserOrganizationsResource } from '~/resources';
import AppPageBanner from './promotion_banners/AppPageBanner.vue';
import AppBannerAd from './promotion_banners/AppBannerAd.vue';
import AppInformationalBanner from './promotion_banners/AppInformationalBanner.vue';
import AppInformationalDialog from './promotion_banners/AppInformationalDialog.vue';

const BANNER_COMPONENT_MAP = new Map([
  ['page_banner', AppPageBanner],
  ['banner_ad', AppBannerAd],
  ['info_banner', AppInformationalBanner],
  ['dialog', AppInformationalDialog],
]);
// Auto register the banner components into the component for easier ergonomics
const COMPONENT_REGISTRY = Object.fromEntries(
  [...BANNER_COMPONENT_MAP.values()].map((component) => {
    return [component.name, component];
  }),
);

export default {
  name: 'AppBanner',
  components: COMPONENT_REGISTRY,
  inheritAttrs: false,
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    append: {
      type: Object,
      required: true,
    },
    userOrganizationId: {
      type: Number,
      required: true,
    },
    feature: {
      type: String,
      required: true,
    },
    promotionType: {
      type: String,
      required: true,
      validator(promotionType) {
        return BANNER_COMPONENT_MAP.has(promotionType);
      },
    },
    bannerProps: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showBanner: true,
    };
  },
  computed: {
    bannerComponent() {
      return this.showBanner ? BANNER_COMPONENT_MAP.get(this.promotionType) : null;
    },
  },
  mounted() {
    // Dialogs attach themselves to the body.
    if (this.append.element && this.promotionType !== 'dialog') {
      this.moveToAnchor();
    }
    // Wait for the DOM to settle
    this.$nextTick(() => {
      this.showBanner = this.show;
    });
  },
  methods: {
    moveToAnchor() {
      const { element, action } = this.append;

      switch (action) {
        case 'prepend':
          element.prepend(this.$el);
          break;
        case 'append':
          element.append(this.$el);
          break;
        case 'before':
          element.parentElement.insertBefore(this.$el, element);
          break;
        case 'after':
          element.parentElement.insertBefore(this.$el, element.nextSibling);
          break;
        default:
          console.error(`${action} is not a valid action; use: ['prepend', 'append', 'before', 'after']`);
          break;
      }
    },
    async close() {
      this.showBanner = false;
      this.$emit('close');
      this.analyticsTrack('Retention banner close', this.bannerProps?.analyticsProps || {});
    },
    async dismissPromotionBanner(promotionType, dismissPayload = {}) {
      try {
        const payload = { dismiss: { feature: this.feature, promotion_type: promotionType, ...dismissPayload } };
        const response = await UserOrganizationsResource.dismissPromotion({ id: this.userOrganizationId }, payload, {
          abortReload: true,
        });

        if (response?.errors) {
          this.$appMessage('Error dismissing promotion banner.');
        } else {
          this.analyticsTrack("Retention banner don't show");
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unexpected error. Please try again.');
        }
      }
    },
    trackCtaClicked() {
      const event = this.bannerProps?.analyticsEvent || 'Retention banner primary CTA';

      this.analyticsTrack(event, this.bannerProps?.analyticsProps || {});
    },
    analyticsTrack(event, extraProps = {}) {
      window.analytics?.track(event, {
        ...extraProps,
        feature: this.feature,
        promotion_type: this.promotionType,
        organization_id: this.bannerProps.organizationId,
        product: this.bannerProps.product || null,
        user_role: this.bannerProps.analyticsInfo.userOrganizationRole,
        organization_plan: this.bannerProps.analyticsInfo.organizationPlan,
        organization_plan_trial: this.bannerProps.analyticsInfo.organizationPlanTrial,
      });
    },
  },
};
</script>
